import {ChecklistContext, IChecklistContext} from '@components/checklist/checklist-context';
import * as React from 'react';
import * as Scroll from 'react-scroll';
import {isNil} from 'lodash';

const checklistContext = React.createContext<IChecklistContext | null>(null);
const useChecklistContext = () => React.useContext(checklistContext);

export const ChecklistProvider = checklistContext.Provider;

const checklistGroupContext = React.createContext<string | null>(null);
const useChecklistGroupContext = () => React.useContext(checklistGroupContext);

export const ChecklistGroup: React.FunctionComponent<{
    name: string;
}> = ({ name, children }) => {
    return (
        <checklistGroupContext.Provider value={name}>
            {children}
        </checklistGroupContext.Provider>
    );
}

interface ChecklistTargetProps {
    name: string;
    valid: boolean;
    /**
     * If `true`, registration of this target will be allowed
     */
    canRegister?: boolean;
    /**
     * Optional group to register the target for
     */
    group?: string;
    /**
     * Optional matching criteria to store with the target
     */
    criteria?: Record<string, unknown>;
    className?: string;
    style?: React.CSSProperties;
}
/**
 * Target for checklists
 * Please see the description for ChecklistContext for caveats
 */
export const ChecklistTarget: React.FunctionComponent<ChecklistTargetProps> = props => {
    const {
        name,
        children,
        valid,
        className,
        style,
        criteria,
        canRegister = true
    } = props;
    let { group } = props;
    const context = useChecklistContext();
    if (!context) {
        throw new Error('ChecklistTarget must be used within a checklist context');
    }

    const groupContext = useChecklistGroupContext();
    if (isNil(group) && groupContext !== null) {
        group = groupContext;
    }

    React.useEffect(() => {
        if (!canRegister) {
            return;
        }

        return context.registerTarget(name, group);
    }, [
        name,
        canRegister,
        group
    ]);
    React.useEffect(() => {
        if (!canRegister) {
            return;
        }

        context.updateValidation(name, valid);
    }, [
        name,
        valid,
        canRegister
    ]);
    React.useEffect(() => {
        if (!canRegister || !criteria) {
            return;
        }

        return context.registerCriteria(name, criteria);
    }, [name, criteria, canRegister])

    return (
        <Scroll.Element name={name}
                        className={className}
                        style={style}>
            {children}
        </Scroll.Element>
    );
}

export const useChecklist = (groups: string[] = []): IChecklistContext => {
    return React.useRef(new ChecklistContext(groups)).current;
};



// WEBPACK FOOTER //
// ./src/components/checklist/index.tsx